import { useCustom, useTranslate } from '@refinedev/core';
import { Alert, Card, Empty, Space, Table, Tag, Typography } from 'antd';
import { format, parseISO } from 'date-fns';
import { enUS, ru } from 'date-fns/locale';
import { weekdayToken } from '../../utils/weekday';
import { useMemo } from 'react';
import { average, dayLinesOf, TIMELINE_FIELDS, type DayLine, type TimelineRow } from './model';

/**
 * E43 · ClientHealthTimeline (buildOrder 11) — "Client Hub — Sleep & activity" (cc-health, chapter "Health Data — Client
 * hub: Sleep & activity (portal, E43)"): 30 days on one time axis — sleep duration and efficiency, daily steps and active
 * minutes, resting heart rate and HRV (always with its metric: SDNN from Apple Health, RMSSD from Health Connect — never
 * compared), check-in mood (E37) and PHQ-9 / GAD-7 points taken in CopingCard (E06) — for the types the client shares
 * with THIS therapist; every type not shared with them shows "not shared". No thresholds, labels or predictions: the
 * clinician reads the data (show, don't diagnose). No download from this section.
 */
type Props = { clientId: string; locale: 'en' | 'ru' };

const TYPES = ['sleep', 'activity', 'heartRate'] as const;

export const ClientHealthTab = ({ clientId, locale }: Props) => {
  const t = useTranslate();
  const dateLocale = locale === 'ru' ? ru : enUS;
  const { data, isFetching, isError } = useCustom<TimelineRow>({
    url: '',
    method: 'get',
    errorNotification: false,
    meta: {
      operation: 'clientHealthTimeline',
      fields: TIMELINE_FIELDS,
      variables: {
        subject: { name: 'subject', type: 'ID!', value: clientId },
        days: { name: 'days', type: 'Int', value: 30 },
      },
    },
    queryOptions: { enabled: Boolean(clientId), retry: false },
  });
  const row = data?.data;
  const lines = useMemo(() => (row ? dayLinesOf(row) : []), [row]);
  const duration = (minutes: number) =>
    t('health.sleep.duration', { hours: Math.floor(minutes / 60), minutes: minutes % 60 });
  const day = (d: string) => format(parseISO(d), `${weekdayToken(locale)} d MMM`, { locale: dateLocale });
  const shared = (type: string) => !!row?.sharedTypes.includes(type);

  if (isError) {
    return <Alert data-testid="cc-health-hub-error" message={t('health.hub.loadError')} showIcon type="error" />;
  }
  if (!row) return <Card loading={isFetching} />;

  const notShared = (type: (typeof TYPES)[number]) => (
    <Card data-testid={`cc-health-hub-card-${type}`} size="small" title={t(`health.types.${type}`)}>
      <Space direction="vertical" size={2}>
        <Tag data-testid={`cc-health-hub-not-shared-${type}`}>{t('health.hub.notShared')}</Tag>
        <Typography.Text type="secondary">
          {t('health.hub.notSharedHint', { type: t(`health.types.${type}`).toLowerCase() })}
        </Typography.Text>
      </Space>
    </Card>
  );

  const nights = row.nights ?? [];
  const activityDays = row.activityDays ?? [];
  const heartDays = row.heartRateDays ?? [];
  const avgAsleep = average(nights.map((n) => n.asleepMinutes));
  const avgEfficiency = average(nights.map((n) => n.efficiency).filter((x): x is number => x !== null));
  const avgSteps = average(activityDays.map((a) => a.stepCount));
  const avgActive = average(activityDays.map((a) => a.activeMinutes));
  const avgResting = average(heartDays.map((h) => h.restingHeartRate).filter((x): x is number => x !== null));
  const metrics = heartDays
    .map((h) => h.hrv?.metric)
    .filter((m, i, all): m is string => !!m && all.indexOf(m) === i);

  const columns = [
    { key: 'day', title: t('health.hub.column.day'), render: (_: unknown, l: DayLine) => day(l.day) },
    ...(shared('sleep')
      ? [
          {
            key: 'asleep',
            title: t('health.hub.column.asleep'),
            render: (_: unknown, l: DayLine) => (l.night ? duration(l.night.asleepMinutes) : '—'),
          },
          {
            key: 'efficiency',
            title: t('health.hub.column.efficiency'),
            render: (_: unknown, l: DayLine) =>
              l.night?.efficiency !== null && l.night?.efficiency !== undefined ? `${l.night.efficiency}%` : '—',
          },
        ]
      : []),
    ...(shared('activity')
      ? [
          {
            key: 'steps',
            title: t('health.hub.column.steps'),
            render: (_: unknown, l: DayLine) => (l.activity ? l.activity.stepCount.toLocaleString(locale) : '—'),
          },
          {
            key: 'active',
            title: t('health.hub.column.activeMinutes'),
            render: (_: unknown, l: DayLine) => (l.activity ? l.activity.activeMinutes : '—'),
          },
        ]
      : []),
    ...(shared('heartRate')
      ? [
          {
            key: 'resting',
            title: t('health.hub.column.restingHeartRate'),
            render: (_: unknown, l: DayLine) => l.heart?.restingHeartRate ?? '—',
          },
          {
            key: 'hrv',
            title: t('health.hub.column.hrv'),
            render: (_: unknown, l: DayLine) =>
              l.heart?.hrv ? `${l.heart.hrv.value} ms (${l.heart.hrv.metric.toUpperCase()})` : '—',
          },
        ]
      : []),
    { key: 'mood', title: t('health.hub.column.mood'), render: (_: unknown, l: DayLine) => l.mood ?? '—' },
    {
      key: 'score',
      title: t('health.hub.column.score'),
      render: (_: unknown, l: DayLine) =>
        l.scores.length
          ? l.scores.map((s) => `${t(`screens.${s.instrument}`)} ${s.score}`).join(', ')
          : '—',
    },
  ];

  return (
    <Space data-testid="cc-health-hub" direction="vertical" size="middle" style={{ width: '100%' }}>
      <Typography.Title level={5}>{t('health.hub.title')}</Typography.Title>
      <Space wrap size="middle" align="start">
        {shared('sleep') ? (
          <Card data-testid="cc-health-hub-card-sleep" size="small" title={t('health.types.sleep')}>
            {nights.length ? (
              <Typography.Text data-testid="cc-health-hub-sleep-summary">
                {t('health.hub.sleepSummary', {
                  asleep: avgAsleep !== null ? duration(avgAsleep) : '—',
                  efficiency: avgEfficiency ?? '—',
                  count: nights.length,
                  days: row.days,
                })}
              </Typography.Text>
            ) : (
              <Typography.Text type="secondary">{t('health.hub.noDataYet')}</Typography.Text>
            )}
          </Card>
        ) : (
          notShared('sleep')
        )}
        {shared('activity') ? (
          <Card data-testid="cc-health-hub-card-activity" size="small" title={t('health.types.activity')}>
            {activityDays.length ? (
              <Typography.Text data-testid="cc-health-hub-activity-summary">
                {t('health.hub.activitySummary', { steps: avgSteps ?? '—', active: avgActive ?? '—', count: activityDays.length })}
              </Typography.Text>
            ) : (
              <Typography.Text type="secondary">{t('health.hub.noDataYet')}</Typography.Text>
            )}
          </Card>
        ) : (
          notShared('activity')
        )}
        {shared('heartRate') ? (
          <Card data-testid="cc-health-hub-card-heartRate" size="small" title={t('health.types.heartRate')}>
            {heartDays.length ? (
              <Space direction="vertical" size={2}>
                <Typography.Text data-testid="cc-health-hub-heart-summary">
                  {t('health.hub.heartSummary', { bpm: avgResting ?? '—', count: heartDays.length })}
                </Typography.Text>
                {metrics.length ? (
                  <Typography.Text type="secondary">
                    {t('health.hub.hrvMetric', { metric: metrics.map((m) => m.toUpperCase()).join(', ') })}
                  </Typography.Text>
                ) : null}
              </Space>
            ) : (
              <Typography.Text type="secondary">{t('health.hub.noDataYet')}</Typography.Text>
            )}
          </Card>
        ) : (
          notShared('heartRate')
        )}
        <Card data-testid="cc-health-hub-card-mood" size="small" title={t('health.hub.mood')}>
          <Typography.Text>{t('health.hub.moodSummary', { count: row.moodByDay.length })}</Typography.Text>
        </Card>
      </Space>
      {lines.length ? (
        <Table<DayLine>
          columns={columns}
          data-testid="cc-health-hub-table"
          dataSource={lines}
          pagination={false}
          rowKey={(l) => l.day}
          size="small"
        />
      ) : (
        <Empty description={t('health.hub.empty')} />
      )}
      <Typography.Text data-testid="cc-health-hub-footer" type="secondary">
        {t('health.hub.footer', {
          shared: row.sharedTypes.length ? row.sharedTypes.map((s) => t(`health.types.${s as 'sleep'}`).toLowerCase()).join(', ') : '—',
          notShared:
            TYPES.filter((x) => !shared(x))
              .map((x) => t(`health.types.${x}`).toLowerCase())
              .join(', ') || '—',
        })}
      </Typography.Text>
    </Space>
  );
};
